import { MODULE_PATH, MODULES, type ModuleId } from './modules'
import { allowedModules, canAccess, type SessionUser } from './session'
import { takeSignOutReason } from './signOut'

// User-facing labels (Portuguese).
export const MODULE_LABEL: Record<ModuleId, string> = {
  one: 'One', // como no protótipo
  emdia: 'Em Dia',
  carreiras: 'Carreiras',
  conquistas: 'Conquistas',
  eventos: 'Eventos',
  conta: 'Minha conta',
}

export interface TopbarItem {
  module: ModuleId
  label: string
  path: string
}

/** Topbar order, as in the prototype (one-home.html). */
const TOPBAR_ORDER: ModuleId[] = ['one', 'conquistas', 'eventos', 'emdia', 'carreiras']

/** Modules shown in the topbar: only those allowed for the profile (Conta lives in the user menu). */
export function topbarModules(user: SessionUser | null): TopbarItem[] {
  const allowed = allowedModules(user)
  return TOPBAR_ORDER.filter((m) => allowed.includes(m)).map((m) => ({
    module: m,
    label: MODULE_LABEL[m],
    path: MODULE_PATH[m],
  }))
}

/** Accepts only internal paths (prevents open redirect via ?volta=). */
export function safeReturnPath(path: string | null | undefined): string {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return '/'
  return path
}

/**
 * Módulo-alvo do caminho de retorno — usado pra marcar o login com a logo do
 * módulo que a pessoa tentou acessar (ONE, Em Dia, Carreiras, Conquistas,
 * Eventos) e pra decidir o redirect. Raiz e caminhos não reconhecidos → 'one'.
 * 'conta' nunca é alvo de marca (cai em 'one').
 */
export function moduleFromPath(path: string): ModuleId {
  const clean = safeReturnPath(path)
  for (const m of MODULES) {
    if (m === 'one' || m === 'conta') continue
    const base = MODULE_PATH[m]
    if (clean === base || clean.startsWith(`${base}/`) || clean.startsWith(`${base}?`)) return m
  }
  return 'one'
}

/**
 * Pra onde ir depois do login: o destino de origem (`volta`) se o perfil tem
 * acesso ao módulo dele; senão, o primeiro módulo permitido na ordem de MODULES
 * (fora 'conta'); sem nenhum, a própria conta. É o "login onde ela tem
 * permissão" (decisão do Lucas, 25/10/2026).
 */
export function postLoginPath(user: SessionUser | null, volta: string): string {
  const alvo = moduleFromPath(volta)
  if (canAccess(user, alvo)) return safeReturnPath(volta)
  const primeiro = MODULES.find((m) => m !== 'conta' && canAccess(user, m))
  return primeiro ? MODULE_PATH[primeiro] : MODULE_PATH.conta
}

/** Sign-in URL that returns to `returnTo` and carries why the session ended (if known). */
export function signInPath(
  returnTo: string,
  opts?: { portal?: string; evento?: string },
): string {
  const params = new URLSearchParams({ volta: safeReturnPath(returnTo) })
  const reason = takeSignOutReason()
  if (reason) params.set('motivo', reason)
  if (opts?.portal) params.set('portal', opts.portal)
  if (opts?.evento) params.set('evento', opts.evento)
  return `/conta/entrar?${params.toString()}`
}
